import React, { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { API_BASE_URL, getApiToken } from "../config/authConfig";

const STATUS_STYLES = {
  Active:  { bg: "#f0fdf4", color: "#166534", border: "#bbf7d0", label: "Active" },
  Pending: { bg: "#fffbeb", color: "#92400e", border: "#fde68a", label: "Pending" },
  Expired: { bg: "#f5f5f5", color: "#6b7280", border: "#e5e7eb", label: "Expired" },
  Removed: { bg: "#fef2f2", color: "#991b1b", border: "#fecaca", label: "Removed" },
};

export default function MyRequests({ onBack }) {
  const { accounts, instance } = useMsal();
  const user = accounts[0] || {};
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("All");

  const fetchRequests = useCallback(async () => {
    try {
      const token = await getApiToken(instance);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.get(`${API_BASE_URL}/getTravelRequests`, {
        params: { userUPN: user.username },
        headers
      });
      const sorted = (res.data.requests || []).sort(
        (a, b) => new Date(b.SubmittedOn) - new Date(a.SubmittedOn)
      );
      setRequests(sorted);
    } catch (err) {
      setError("Failed to load travel requests.");
    } finally {
      setLoading(false);
    }
  }, [user.username, instance]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const filtered = filter === "All" ? requests : requests.filter(r => r.Status === filter);

  const counts = {
    All: requests.length,
    Active: requests.filter(r => r.Status === "Active").length,
    Pending: requests.filter(r => r.Status === "Pending").length,
    Expired: requests.filter(r => r.Status === "Expired").length,
    Removed: requests.filter(r => r.Status === "Removed").length,
  };

  return (
    <div className="page">
      <header className="app-header">
        <div className="header-inner">
          <div className="header-logo">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="8" fill="#230658" />
              <path d="M9 16L14 21L23 11" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Travel Request Portal</span>
          </div>
          <div className="header-user">
            <div className="user-avatar">{user.name ? user.name[0].toUpperCase() : "U"}</div>
            <span>{user.name}</span>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="requests-title-row">
          <div>
            <h2 className="page-title">My travel requests</h2>
            <p className="page-subtitle">{user.username}</p>
          </div>
          <button className="btn-primary" onClick={onBack}>+ New request</button>
        </div>

        <div className="filter-tabs">
          {["All", "Active", "Pending", "Expired", "Removed"].map((f) => (
            <button key={f} className={`filter-tab ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>
              {f}<span className="filter-count">{counts[f]}</span>
            </button>
          ))}
        </div>

        {loading && <div className="requests-loading"><div className="spinner" /><p>Loading your requests...</p></div>}
        {error && <div className="error-banner">{error}</div>}

        {!loading && !error && filtered.length === 0 && (
          <div className="empty-state">
            <p>No {filter !== "All" ? filter.toLowerCase() : ""} travel requests found.</p>
            <button className="btn-primary" onClick={onBack}>Submit your first request</button>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="requests-list">
            {filtered.map((req, i) => {
              const style = STATUS_STYLES[req.Status] || STATUS_STYLES.Active;
              return (
                <div key={i} className={`request-card ${req.Status === "Expired" || req.Status === "Removed" ? "faded" : ""}`}>
                  <div className="request-card-header">
                    <div className="request-country-row">
                      <div className="trip-code-badge sm">{req.Country?.slice(0,2).toUpperCase()}</div>
                      <strong className="request-country">{req.Country}</strong>
                      <span className="status-badge" style={{ background: style.bg, color: style.color, border: `1px solid ${style.border}` }}>
                        {style.label}
                      </span>
                    </div>
                    <span className="request-submitted">Submitted {formatDate(req.SubmittedOn)}</span>
                  </div>
                  <div className="request-dates-row">
                    <div className="request-date-block">
                      <span className="date-label">Departure</span>
                      <span className="date-value">{formatDate(req.TravelStartDate)}</span>
                    </div>
                    <div className="date-divider">→</div>
                    <div className="request-date-block">
                      <span className="date-label">Return</span>
                      <span className="date-value">{formatDate(req.TravelEndDate)}</span>
                    </div>
                    <div className="request-duration">{getDuration(req.TravelStartDate, req.TravelEndDate)} days</div>
                  </div>
                  <div className="request-group-row">
                    <span className="group-label">Security group</span>
                    <code className="group-value">{req.SecurityGroup}</code>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getDuration(start, end) {
  if (!start || !end) return "—";
  const diff = new Date(end) - new Date(start);
  return Math.max(1, Math.round(diff / (1000 * 60 * 60 * 24)));
}